"use client";

import { useEffect } from "react";
import { X, ChevronLeft, ChevronRight, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import type { Marque } from "@/data/catalog";
import type { PanierItem } from "./types";
import { formatPrix } from "./types";

/* ─────────────── Couronne impériale (SVG) ─────────────── */
export function Crown({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 40" fill="none" className={className} aria-hidden="true">
      <defs>
        <linearGradient id="orCouronne" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#F6E27A" />
          <stop offset="45%" stopColor="#D4AF37" />
          <stop offset="100%" stopColor="#A97E14" />
        </linearGradient>
      </defs>
      <path
        d="M6 32 L4 12 L16 22 L26 6 L32 14 L38 6 L48 22 L60 12 L58 32 Z"
        fill="url(#orCouronne)"
        stroke="#7A5D10"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <rect x="6" y="33" width="52" height="4.5" rx="1.5" fill="url(#orCouronne)" stroke="#7A5D10" strokeWidth="1.2" />
      <circle cx="32" cy="5" r="2.6" fill="#F6E27A" stroke="#7A5D10" strokeWidth="1" />
      <circle cx="4.5" cy="10.5" r="2.1" fill="#F6E27A" stroke="#7A5D10" strokeWidth="1" />
      <circle cx="59.5" cy="10.5" r="2.1" fill="#F6E27A" stroke="#7A5D10" strokeWidth="1" />
    </svg>
  );
}

/* ─────────────── Toast doré ─────────────── */
export function FlashToast({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-6 left-1/2 z-[90] -translate-x-1/2 px-6 py-3 rounded-full bg-[#170d02] border border-[#D4AF37]/60 shadow-[0_10px_40px_rgba(212,175,55,0.35)] text-[#F6E27A] text-sm font-medium max-w-[92vw] text-center"
    >
      {message}
    </div>
  );
}

/* ─────────────── Visionneuse produit (lightbox) ─────────────── */
interface LightboxProps {
  marque: Marque;
  index: number;
  onClose: () => void;
  onNav: (dir: 1 | -1) => void;
  onAdd: (p: Produit) => void;
}

export function ProductLightbox({ marque, index, onClose, onNav, onAdd }: LightboxProps) {
  const p = marque.produits[index];

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, onNav]);

  if (!p) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={p.name}
    >
      <div
        className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto empire-scroll rounded-2xl border border-[#D4AF37]/40 bg-[#120B18] shadow-[0_30px_120px_rgba(0,0,0,0.7)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Fermer la visionneuse"
          className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/50 border border-[#D4AF37]/40 text-[#F6E27A] hover:bg-[#D4AF37] hover:text-[#170d02] transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="grid md:grid-cols-2">
          <div className="relative bg-black/40 flex items-center justify-center p-4">
            <img
              src={p.file}
              alt={p.name + " — " + marque.nom}
              className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
            />
          </div>
          <div className="p-6 md:p-8 flex flex-col gap-4">
            <div>
              <span
                className="inline-block text-[11px] tracking-[0.2em] uppercase px-3 py-1 rounded-full border"
                style={{
                  borderColor: marque.couleur,
                  color: marque.texte,
                  background: marque.couleur + "33",
                }}
              >
                {marque.nom}
              </span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-[#F5EFE0] leading-snug">
              {p.name}
            </h3>
            <p className="text-[#D4AF37] text-lg font-medium">
              {p.contenance ? p.contenance : "Création de prestige"}
              <span className="text-[#F5EFE0]/40"> · N° {String(p.num).padStart(2, "0")}</span>
            </p>
            <div className="gold-line" />
            <p className="text-3xl font-display font-bold gold-text">
              {formatPrix(3.9 + ((p.num * 37) % 46))}
            </p>
            <p className="text-sm text-[#F5EFE0]/60 italic">
              « Le Premier et le Meilleur est Adji Anta Diop » — signature apposée sur
              chaque étiquette.
            </p>
            <button
              onClick={() => onAdd(p)}
              className="gold-btn mt-2 w-full py-3 rounded-xl font-semibold tracking-wide flex items-center justify-center gap-2"
            >
              <ShoppingBag className="h-5 w-5" />
              Ajouter au panier impérial
            </button>
            <div className="flex items-center justify-between mt-2">
              <button
                onClick={() => onNav(-1)}
                className="flex items-center gap-1 text-sm text-[#F5EFE0]/70 hover:text-[#D4AF37] transition-colors"
                aria-label="Création précédente"
              >
                <ChevronLeft className="h-4 w-4" /> Précédente
              </button>
              <span className="text-xs text-[#F5EFE0]/40">
                {index + 1} / {marque.produits.length}
              </span>
              <button
                onClick={() => onNav(1)}
                className="flex items-center gap-1 text-sm text-[#F5EFE0]/70 hover:text-[#D4AF37] transition-colors"
                aria-label="Création suivante"
              >
                Suivante <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────── Panier impérial (tiroir latéral) ─────────────── */
interface PanierProps {
  open: boolean;
  items: PanierItem[];
  onClose: () => void;
  onQty: (id: string, delta: number) => void;
  onRemove: (id: string) => void;
  onCommander: () => void;
}

export function PanierDrawer({ open, items, onClose, onQty, onRemove, onCommander }: PanierProps) {
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  if (!open) return null;
  const total = items.reduce((a, i) => a + i.prix * i.qte, 0);

  return (
    <div className="fixed inset-0 z-[85]" role="dialog" aria-modal="true" aria-label="Panier impérial">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-[#120B18] border-l border-[#D4AF37]/35 shadow-[0_0_80px_rgba(0,0,0,0.8)] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#D4AF37]/25">
          <h2 className="font-display text-xl font-bold gold-text flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-[#D4AF37]" />
            Panier Impérial
          </h2>
          <button
            onClick={onClose}
            aria-label="Fermer le panier"
            className="p-2 rounded-full border border-[#D4AF37]/40 text-[#F6E27A] hover:bg-[#D4AF37] hover:text-[#170d02] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto empire-scroll px-5 py-4 space-y-4">
          {items.length === 0 && (
            <p className="text-center text-[#F5EFE0]/50 italic pt-16">
              Votre panier attend ses premiers trésors…
            </p>
          )}
          {items.map((i) => (
            <div key={i.id} className="flex gap-3 items-center card-empire rounded-xl p-3">
              <img src={i.image} alt={i.nom} className="h-16 w-16 rounded-lg object-cover border border-[#D4AF37]/30" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#F5EFE0] truncate">{i.nom}</p>
                <p className="text-xs text-[#D4AF37]">{i.marque}</p>
                <p className="text-sm text-[#F5EFE0]/80 mt-0.5">{formatPrix(i.prix)}</p>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => onQty(i.id, -1)}
                    aria-label={"Retirer un " + i.nom}
                    className="p-1 rounded border border-[#D4AF37]/40 text-[#F6E27A] hover:bg-[#D4AF37] hover:text-[#170d02]"
                  >
                    <Minus className="h-3 w-3" />
                  </button>
                  <span className="w-6 text-center text-sm">{i.qte}</span>
                  <button
                    onClick={() => onQty(i.id, 1)}
                    aria-label={"Ajouter un " + i.nom}
                    className="p-1 rounded border border-[#D4AF37]/40 text-[#F6E27A] hover:bg-[#D4AF37] hover:text-[#170d02]"
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                </div>
                <button
                  onClick={() => onRemove(i.id)}
                  aria-label={"Supprimer " + i.nom}
                  className="text-[#F5EFE0]/40 hover:text-red-400 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-[#D4AF37]/25 px-5 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[#F5EFE0]/70 text-sm">Total impérial</span>
            <span className="font-display text-2xl font-bold gold-text">{formatPrix(total)}</span>
          </div>
          <button
            onClick={onCommander}
            disabled={items.length === 0}
            className="gold-btn w-full py-3 rounded-xl font-semibold tracking-wide disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Commander — Livraison Impériale
          </button>
          <p className="text-[11px] text-center text-[#F5EFE0]/40 italic">
            Boutique de démonstration — aucune transaction réelle.
          </p>
        </div>
      </aside>
    </div>
  );
}
